import { computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { useDashboard } from '@rdrive/plugin-dashboard-shell';
import { useDuckDBExport } from '@rdrive/data-duckdb';
import { useAnalytics } from '@/utils/useAnalytics';
import { useIssueStatusFilters, OVERDUE_TOGGLE } from '@/composables/useIssueStatusFilters';

// The app's root component — an adapter over DashboardShell. The PLATFORM half of the boot — URL
// validation → settings, analytics init, the user id, token refresh (new for issue-analysis),
// DuckDB init — and every platform service are the dashboard's (createDashboard / useDashboard()
// from @rdrive/plugin-dashboard-shell); the data load is its loader over src/data/manifest.js.
// This file keeps the app's half: theme, the welcome dialog's flags and Load button, the data
// load's trigger, the DuckDB export, the FAB.
export default () => {
  // The shared welcome dialog's opt-in "Discover what's new from RDrive" panel (cards + the
  // rdrive.io item pages they open; @rdrive/plugin-welcome-dialog's `showMarketingPanel`).
  // OFF for now (2026-10-05): the dialog is then the hero alone — the same card project
  // overview shows — and the ~260 marketing-page/Freshchat requests are never made.
  const SHOW_WELCOME_MARKETING_PANEL = false;

  const store = useStore();
  const dashboard = useDashboard();
  const { t } = useI18n();
  const { trackEvent } = useAnalytics();
  const vuetifyTheme = useTheme();
  const currentTheme = computed(() => store.state.theme);

  // The boot context, parsed once from the iframe URL when the dashboard was created
  // (usePlatformParams). Read here for `theme` and the welcome dialog's `queryString`; the
  // dashboard's start() (onMounted) validates and applies the rest.
  const platformParams = dashboard.params;

  // The initial theme: the host's `theme` URL param first (HOST_APP_CONVENTIONS §3.1 — its OS
  // colour scheme; the host recreates the iframe when it changes), exactly `light`/`dark` or
  // ignored; else the OS scheme via matchMedia, as before. Dark is pushed to Vuetify at once
  // either way, so the first paint is already dark. (project-overview's policy, 6b-2a.)
  const getUserColorScheme = () => {
    if (platformParams.theme) {
      if (platformParams.theme === 'dark') {
        vuetifyTheme.global.name.value = 'dark'; //set immediately
      }
      return platformParams.theme;
    }
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        vuetifyTheme.global.name.value = 'dark'; //set immediately
        return 'dark';
      } else {
        return 'light';
      }
    } else {
      return currentTheme.value;
    }
  };

  const theme = ref(getUserColorScheme());
  store.commit('setTheme', theme.value);

  // What DashboardShell's persistent missing-params dialog lists — the dashboard's, set by
  // start(). The dialog's and the DuckDB query dialog's open states live in the shell.
  const missingParams = dashboard.missingParams;

  const menuItems = computed(() => [
    { name: 'Project', icon: 'mdi-home-outline', text: t('overview') },
    { name: 'Drawings', icon: 'mdi-map', text: t('drawings') },
    { name: 'Analysis', icon: 'mdi-poll', text: t('analysis') }
  ]);

  const settings = computed(() => dashboard.settings);
  const loadDashboard = computed(() => store.getters.getLoadDashboard);

  const buildMode = ref('production');

  // The welcome dialog's progress comes from the dashboard's data loader: `done` counts every
  // boot table that settled, `total` is the manifest's boot tables (15 — src/data/manifest.js;
  // the hand-counted `totalApiRequests = 15` is gone).
  const dataLoader = dashboard.loader;
  const apiProgress = computed(() => dataLoader.progress.done);
  const totalApiRequests = computed(() => dataLoader.progress.total);

  // --- DuckDB query dialog ------------------------------------------------
  // DashboardShell renders @rdrive/plugin-duckdb-query-dialog's dialog from its kebab; the app
  // runs the export (the same EXPORT DATABASE → zip issue-analysis's own dialog copy ran). `db`
  // only exists once DuckDB has initialised: the dashboard's `db` ref is set then.
  const { exporting, exportDatabase } = useDuckDBExport({
    conn: computed(() => dashboard.conn),
    db: dashboard.db
  });

  // --- Welcome dialog (@rdrive/plugin-welcome-dialog, rendered by DashboardShell) ------
  const showWelcomeDialog = computed({
    get: () => store.getters.getShowWelcomeDialog,
    set: (value) => store.dispatch('updateShowWelcomeDialog', value)
  });
  const isDuckDBInitialized = computed(() => dashboard.isDuckDBInitialized.value);
  const projectName = computed(() => dashboard.settings.projectName);
  const dataLoaded = computed(() => dashboard.dataLoaded.value);
  const queryString = computed(() => platformParams.queryString);

  const handleLoadDashboard = async () => {
    trackEvent('loadDashboard', {
      action_type: 'button_click',
      project_code: dashboard.settings.projectCode,
      project_name: dashboard.settings.projectName
    });

    await store.dispatch('updateLoadDashboard', true);
  };

  const handleCopyQueryString = () => {
    trackEvent('copyQueryString', {
      action_type: 'avatar_double_click',
      project_code: dashboard.settings.projectCode,
      project_name: dashboard.settings.projectName
    });
  };

  watch(dataLoaded, (newValue) => {
    if (newValue) {
      store.dispatch('updateShowWelcomeDialog', false);
    }
  });

  // --- Global status / "overdue only" filters (@rdrive/plugin-status-filter's FAB) ---------
  // The FAB's filters on BOTH filter contexts: composables/
  // useIssueStatusFilters.js holds the logic and the SQL (it was the store-coupled
  // components/StatusFilterFab.vue until IA_ABSORPTION_PLAN Phase 4 step 13.2). Writable
  // computeds over the store's FAB state keep the composable store-free; the contexts are
  // computeds over the dashboard's shallowReactive `filters`, so a replaced context reaches it.
  const showFilterDialog = ref(false);
  const statusFilters = computed({
    get: () => store.state.statusFilters,
    set: (value) => store.commit('setStatusFilters', value)
  });
  const overdueOnly = computed({
    get: () => store.state.overdueOnly,
    set: (value) => store.commit('setOverdueOnly', value)
  });
  const {
    statusTemplates, allFiltersSelected, hasActiveFilter, extraToggleValues,
    toggleStatus, toggleAll, toggleExtra
  } = useIssueStatusFilters({
    conn: computed(() => dashboard.conn),
    dataLoaded: dashboard.dataLoaded,
    isInitialized: dashboard.isDuckDBInitialized,
    contexts: {
      issues: computed(() => dashboard.filters.issues),
      statusHistory: computed(() => dashboard.filters.statusHistory)
    },
    statusTemplatesObj: computed(() => store.getters.getStatusTemplatesObj),
    statusFilters,
    overdueOnly
  });
  // The package renders the checkbox; this app owns its label (a `filterDialog` key of its
  // src/locales/ — `t` is reactive, so it follows a locale switch) and colour, as before.
  const extraToggles = computed(() => [
    { key: OVERDUE_TOGGLE, label: t('filterDialog.overdueOnly'), color: 'orange' }
  ]);

  // DashboardShell has already closed its missing-params dialog when it emits this.
  const handleMissingParams = () => {
    window.close();
  };

  const getData = async () => {
    try {
      if (!dashboard.isDuckDBInitialized.value) {
        console.error('DuckDB not initialized yet. Please wait for initialization to complete.');
        return;
      }

      if (!dashboard.conn) {
        throw new Error('DuckDB connection not initialized');
      }

      // The whole load is the manifest's (src/data/manifest.js), run by the dashboard's data
      // loader: the 15 boot tables in parallel, status_history_temporal + the status_templates
      // re-rank (ddl.post), then the config's onLoaded hook — both filter contexts, the logo and
      // the status templates — and only THEN dataLoaded = true, which closes the welcome dialog
      // and starts every component's queries. A failed table is logged by the loader and the
      // load carries on, as it always did.
      await dataLoader.load();

      store.commit('setShowWelcomeDialog', false);
      store.commit('setLoadDashboard', false);
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error fetching data:`, error);
    }
  };

  const toggleTheme = () => {
    theme.value = theme.value === 'dark' ? 'light' : 'dark';
    store.commit('setTheme', theme.value);
    vuetifyTheme.global.name.value = theme.value;
  };

  const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  colorSchemeQuery.addEventListener('change', (e) => {
    theme.value = e.matches ? 'dark' : 'light';
    vuetifyTheme.global.name.value = theme.value;
  });

  watch(
    () => [
      settings.value.scopedToken,
      settings.value.projectCode,
      loadDashboard.value
    ],
    ([newScopedToken, newProjectCode, newLoadDashboard]) => {
      if (newScopedToken && newProjectCode && newLoadDashboard) {
        getData();
      }
    }
  );

  const updateTheme = (e) => {
    theme.value = e.matches ? 'dark' : 'light';
    store.commit('setTheme', theme.value);
  };

  const handleItemsUpdated = () => {
    // Handle items updated event
    //console.log('Items updated event received');
  };

  onMounted(async () => {
    buildMode.value = 'webapp';

    // The platform half of the boot is the dashboard's: it validates the URL first, then
    // writes the settings, configures analytics, logs the user id, arms the token refresh and
    // initialises DuckDB. A missing param stops it right after its console.error, while the
    // shell's dialog shows — and stops this hook too.
    const started = await dashboard.start();
    if (!started) {
      return; // Stop further initialization if parameters are invalid
    }

    store.dispatch('updateBuildMode', buildMode.value);
    store.commit('setTheme', theme.value);
    colorSchemeQuery.addEventListener('change', updateTheme);
    // Fullscreen (the kebab's toggle and the fullscreenchange listeners) is DashboardShell's.
  });

  onUnmounted(() => {
    dashboard.stop(); // the token refresher
    colorSchemeQuery.removeEventListener('change', updateTheme);
  });

  return {
    SHOW_WELCOME_MARKETING_PANEL,
    currentTheme,
    missingParams,
    menuItems,
    apiProgress,
    totalApiRequests,
    exporting,
    exportDatabase,
    showWelcomeDialog,
    isDuckDBInitialized,
    projectName,
    dataLoaded,
    queryString,
    handleLoadDashboard,
    handleCopyQueryString,
    showFilterDialog,
    statusFilters,
    statusTemplates,
    allFiltersSelected,
    hasActiveFilter,
    extraToggleValues,
    toggleStatus,
    toggleAll,
    toggleExtra,
    extraToggles,
    handleMissingParams,
    toggleTheme,
    handleItemsUpdated
  };
};
